import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CircularProgress,
  Chip,
  Pagination,
  Rating,
  Skeleton,
  Alert,
} from "@mui/material";
import FiberNewIcon from "@mui/icons-material/FiberNew";
import WishlistToggle from "./WishlistToggle";
import { fetchNewArrivals } from "../api/productApi";

const PAGE_SIZE = 24;

// ── helpers ────────────────────────────────────────────────────────────────────
const parseSizes = (sizeList) => {
  if (!sizeList) return [];
  try {
    return JSON.parse(sizeList);
  } catch {
    return [];
  }
};

const mapProduct = (item) => {
  const price           = parseFloat(item.price) || 0;
  const discountedPrice = parseFloat(item.discounted_price) || price;
  const isOnPromotion   = discountedPrice < price;
  return {
    id:              item.article_id,
    name:            item.enhanced_prod_name || item.article_id,
    detail_desc:     item.enhanced_description || "",
    price,
    discounted_price: discountedPrice,
    isOnPromotion,
    image:           item.image_url?.replace("storage.cloud.google.com", "storage.googleapis.com").trim(),
    gender:          item.gender,
    sizes:           parseSizes(item.size_list),
    rating:          Math.random() * 2 + 3,
    reviews:         Math.floor(Math.random() * 100) + 1,
  };
};

// ── ProductCard ────────────────────────────────────────────────────────────────
function ProductCard({ product }) {
  const navigate = useNavigate();
  return (
    <Card
      onClick={() => navigate(`/item/${product.id}`)}
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        cursor: "pointer",
        position: "relative",
        borderRadius: 2,
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        transition: "all 0.25s ease",
        "&:hover": { transform: "translateY(-4px)", boxShadow: "0 8px 20px rgba(0,0,0,0.13)" },
      }}
    >
      {/* Badge */}
      <Chip
        label="New"
        size="small"
        sx={{
          position: "absolute", top: 10, left: 10, zIndex: 1,
          bgcolor: "#4CAF50", color: "#fff", fontWeight: 700, fontSize: "0.68rem", height: 22,
        }}
      />

      {/* Image */}
      <CardMedia
        component="img"
        image={product.image || `https://picsum.photos/seed/${product.id}/400/600`}
        alt={product.name}
        sx={{ objectFit: "cover", height: 300, bgcolor: "#f5f5f5" }}
        onError={(e) => { e.target.src = `https://picsum.photos/seed/${product.id}/400/600`; }}
      />

      {/* Info */}
      <CardContent sx={{ flexGrow: 1, p: 1.5, pb: "12px !important" }}>
        {/* Name + Wishlist */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 0.5 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500, flex: 1, mr: 0.5,
              overflow: "hidden", textOverflow: "ellipsis",
              display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
              "&:hover": { color: "primary.main" },
            }}
          >
            {product.name}
          </Typography>
          <Box sx={{ flexShrink: 0, mt: "-2px" }}>
            <WishlistToggle product={product} />
          </Box>
        </Box>

        {/* Rating */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 0.75 }}>
          <Rating value={product.rating} precision={0.1} size="small" readOnly sx={{ color: "#FFB400", fontSize: "0.95rem" }} />
          <Typography variant="caption" sx={{ ml: 0.5, color: "#666", fontSize: "0.72rem" }}>
            ({product.reviews})
          </Typography>
        </Box>

        {/* Price */}
        <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75, mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem", color: product.isOnPromotion ? "#FF4444" : "#1a1a1a" }}>
            ${product.isOnPromotion ? product.discounted_price.toFixed(2) : product.price.toFixed(2)}
          </Typography>
          {product.isOnPromotion && (
            <Typography variant="body2" sx={{ textDecoration: "line-through", color: "#999", fontSize: "0.82rem" }}>
              ${product.price.toFixed(2)}
            </Typography>
          )}
        </Box>

        {/* Sizes */}
        {product.sizes.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
            {product.sizes.slice(0, 5).map((s) => (
              <Chip
                key={s} label={s} size="small"
                sx={{ height: 20, fontSize: "0.65rem", border: "1px solid #ddd", bgcolor: "transparent", cursor: "pointer" }}
              />
            ))}
            {product.sizes.length > 5 && (
              <Typography variant="caption" sx={{ color: "#999", fontSize: "0.65rem", alignSelf: "center" }}>
                +{product.sizes.length - 5}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ── Skeleton grid while loading ────────────────────────────────────────────────
function ProductSkeleton() {
  return (
    <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
      {Array.from({ length: PAGE_SIZE }).map((_, i) => (
        <Grid item xs={6} sm={4} md={3} lg={2.4} key={i}>
          <Box sx={{ borderRadius: 2, overflow: "hidden" }}>
            <Skeleton variant="rectangular" height={300} />
            <Box sx={{ p: 1.5 }}>
              <Skeleton width="80%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton width="50%" height={16} sx={{ mb: 0.75 }} />
              <Skeleton width="40%" height={22} />
            </Box>
          </Box>
        </Grid>
      ))}
    </Grid>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function NewArrivalsPage() {
  const [products,     setProducts]     = useState([]);
  const [page,         setPage]         = useState(1);
  const [totalPages,   setTotalPages]   = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState(null);

  const loadPage = useCallback(async (pageNum) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchNewArrivals({ page: pageNum, pageSize: PAGE_SIZE });
      setProducts((data.products || []).map(mapProduct));
      setTotalPages(data.total_pages || 1);
      setTotalRecords(data.total_records || 0);
    } catch (err) {
      console.error("New arrivals load error:", err);
      setError(err.message || "Failed to load new arrivals.");
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPage(page);
  }, [page, loadPage]);

  const handlePageChange = (_e, value) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
        <FiberNewIcon sx={{ color: "#4CAF50", fontSize: 28 }} />
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
          New Arrivals
        </Typography>
      </Box>

      {!loading && !error && totalRecords > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {totalRecords.toLocaleString()} products · page {page} of {totalPages}
        </Typography>
      )}

      {/* ── Error ── */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* ── Grid ── */}
      {loading ? (
        <ProductSkeleton />
      ) : products.length === 0 && !error ? (
        <Box sx={{ textAlign: "center", py: 10 }}>
          <Typography variant="h6" color="text.secondary">No products found.</Typography>
        </Box>
      ) : (
        <Grid container spacing={{ xs: 2, sm: 2.5, md: 3 }}>
          {products.map((product) => (
            <Grid item xs={6} sm={4} md={3} lg={2.4} key={product.id}>
              <ProductCard product={product} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Pagination ── */}
      {!loading && totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 5, gap: 2 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            showFirstButton
            showLastButton
            siblingCount={1}
            boundaryCount={1}
            sx={{
              "& .MuiPaginationItem-root": { borderRadius: 1.5 },
              "& .Mui-selected": { fontWeight: 700 },
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
            {((page - 1) * PAGE_SIZE + 1).toLocaleString()}–
            {Math.min(page * PAGE_SIZE, totalRecords).toLocaleString()} of {totalRecords.toLocaleString()}
          </Typography>
        </Box>
      )}
    </Container>
  );
}
